// ** React Imports
import { Fragment, useState , useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,  
  Menu,
  MenuItem,
  List,
  ListItem,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Backdrop,
} from "@mui/material";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";
import OptionsMenu from "../../components/option-menu";

// ** Third Party Imports
import { useDropzone } from 'react-dropzone'
import Swal from 'sweetalert2';
import withReactContent from "sweetalert2-react-content";
import NProgress from "react-nprogress";

// ** Utils Import
import { hexToRGBA } from "../../components/utils/hex-to-rgba";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Icons Import
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import ClearIcon from '@mui/icons-material/Clear';
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Icon from "../../components/icon";

// Importing Logic to maintain the code 
import { GetEmployees } from "./logic/GetEmployees";
import { formatDate, getDate, formatDateForMySQL } from "../../components/formatDate";
import { filterData } from "./logic/filterData";
import { DeleteEmployee } from "./logic/DeleteEmployee";
import { exportToCSV } from "../../components/export-components/ExportToCSV";
import { GetDepartments } from "./logic/GetDepartments";
import { SaveEmployee } from "./logic/SaveEmployee";
import { getUserName } from "../../utils/auth";

// Component Imports
// import AddUpdateEmployeeDialog from "./component/AddUpdateEmployee";
import TagManagementDialog from '../Customers/component/TagManagement';
import TimeDropdown from './component/TimeDropdown';
import ExcelCSVImport from '../../components/ExcelCSVImport';
import ExportToExcel from "../../components/export-components/ExportExcel";
import ExportToPdf from "../../components/export-components/ExportPdf";
import BulkImport from "../../components/BulkImport";
import { ExportFilter }from "./logic/ExportFilter";
import {Visibility as EyeIcon, Edit as PencilIcon, Delete as TrashIcon } from '@mui/icons-material';
import AdvancedFilter from "./component/AdvancedFilter";
import AddEmployeeDialog from "./component/AddEmployee";

import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SaveBulkEmployee } from "./logic/BulkSave";
import _ from 'lodash';
import { useEmployeePermission } from "./permissions/EmployeePermissions";

const MySwal = withReactContent(Swal);

const CustomChipWithDropdown = ({ tag, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedTag) => {
    onSelect(selectedTag);
    handleClose();
  };

  return (
    <Box>
      <CustomChip
        rounded
        size="small"
        skin="outlined"
        sx={{ lineHeight: 1 }}
        color="info"
        label={tag}
        onClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 200 } }}
      >
        <MenuItem onClick={() => handleSelect(tag)}>Select</MenuItem>
      </Menu>
    </Box>
  );
};

const steps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:address-book",
    title: "Employment Details",
  },
  {
    icon: "tabler:align-box-bottom-center",
    title: "Attachments",
  },
];

const keysToKeep = [
  'employeeID',
  'empType',
  'firstName',
  'address',
  'createDate',
  'jobTitle',
  'modifiedDate',
  'modifiedBy',
  'referredBy',
  'isActive',
  'tags',
  'email',
];

const FileUploader = () => {
      // ** State
  const [files, setFiles] = useState([])

  // ** Hooks
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => {
      setFiles(acceptedFiles.map(file => Object.assign(file)))
    }
  })

  const renderFilePreview = file => {
    if (file.type.startsWith('image')) {
      return <img width={38} height={38} alt={file.name} src={URL.createObjectURL(file)} />
    } else {
      return <Icon icon='tabler:file-description' />
    }
  }

  const handleRemoveFile = file => {
    const uploadedFiles = files
    const filtered = uploadedFiles.filter(i => i.name !== file.name)
    setFiles([...filtered])
  }

  const fileList = files.map(file => (
    <ListItem key={file.name}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'space-between', border: '1px solid #E1E4EA', p: 2, borderRadius: 2}}>
            <Box>{renderFilePreview(file)}</Box>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Typography className='file-name'>{file.name}</Typography>
                <Typography className='file-size' variant='body2'>
                    {Math.round(file.size / 100) / 10 > 1000
                    ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
                    : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
                </Typography>
            </Box>
            <Box>
                <IconButton onClick={() => handleRemoveFile(file)}>
                    <Icon icon='tabler:trash' fontSize={20} />
                </IconButton>
            </Box>
        </Box>
    </ListItem>
  ))

  const handleRemoveAllFiles = () => {
    setFiles([])
  }

    return (
        <Fragment>
            <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <Box sx={{ display: 'flex', justifyContent: 'center', backgroundColor: '#F5F7FA', p: 4, width: '100%', cursor: 'pointer', borderRadius: 1 }}>
                    <CloudUploadIcon sx={{color: '#454F64'}} />
                    <Typography sx={{color: '#454F64', ml: 4}}>
                        Drop files to attach or click here to browse
                    </Typography>
                </Box>
            </div>
            {files.length ? (
                <Fragment>
                <List>{fileList}</List>
                <div className='buttons'>
                    <Button color='error' size='small' variant='outlined' onClick={handleRemoveAllFiles}>
                    Remove All
                    </Button>
                </div>
                </Fragment>
            ) : null}
        </Fragment>
    )
}

const Employees = () => {
 
  //#region Permissions
  const { EmployeePermissionProps } = useEmployeePermission();
  const {
    hasEmployeeSavePermission,
    hasEmployeeGetPermission,
    hasEmployeeDeletePermission,
    hasEmployeeGetDepartmentsPermission,
    hasEmployeeUpdatePersonalDetailPermission,
    hasEmployeeSearchPermission,
  } = EmployeePermissionProps;
  //#endregion Permissions

  const theme = useTheme();
  const contentHeightFixed = false;
  const smAbove = useMediaQuery(theme.breakpoints.up("sm"));
  const smBelow = useMediaQuery(theme.breakpoints.down("sm"));
  const tagManagementData = { title: 'Tag Management' }
  
  //#region States
  const [selectedButton, setSelectedButton] = useState(0);
  const [loading, setLoading] = useState(false);  
  const [btnLoader, setBtnLoader] = useState(false);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openNewCustomerDlg, setOpenNewCustomerDlg] = useState(false);
  const [openBulkImportDlg, setOpenBulkImportDlg] = useState(false);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [phoneFields, setPhoneFields] = useState([
    { id: 1, value: "", primary: false },
  ]);
  const [modalData, setModalData] = useState({title:'Create Employee', btnTxt:'Create Employee', successTitle:'Create Employee', successTxt: 'Employee has been successfully created.'});
  const [bulkData, setBulkData] = useState(null);
  const initialEmployeeState  = {
    address: '',
    city: '',
    clockInOutRequired: false,
    companyID: 0,
    country: '',
    county: '',
    createBy: null,
    createDate: formatDateForMySQL(new Date()),
    dateOfBirth: '',
    departmentID: 0,
    drivingLicenseExpiryDate: '',
    drivingLicenseValid: false,
    email: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    employeeID: 0,
    employmentStatus: '',
    firstName: '',
    hireDate: '',
    isActive: true,
    jobTitle: '',
    lastName: '',
    modifiedBy: null,
    modifiedDate: null,
    phoneNumber: '',
    position: '',
    postalCode: '',
    probationEndDate: '',
    referredBy: '',
    stateProvince: '',
    title: 'Mr',
    userID: 0,
    wageAmount: 0,
    wageType: '',
    empType:'',
    weeklyHours:0,
    overtimeExempt:true,
    phoneNumbers:[],
    tags:[],
    employeeHours:[]
  }
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRows, setSelectedRows] = useState(0);
  const [employee, setEmployee] = useState(initialEmployeeState);
  const [errors, setErrors] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [AllEmployees , setAllEmployees]  = useState();
  const resetEmployee = () => {
    setEmployee(initialEmployeeState);

  };
  const [isEditing, setIsEditing] = useState(false);  
  const [EmpFiles, setEmpFiles] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [bulkEmpType, setBulkEmpType] = useState(0);
  const [bulkEmpStatus, setBulkEmpStatus] = useState(0);
  const [bulkTags, setBulkTags] = useState([]);
  // const [paginationText, setPaginationText] = useState('');
  //#endregion States

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    if(hasEmployeeGetPermission){
      fetchData();
    }
  }, [hasEmployeeGetPermission]);

  const fetchData = async () => {
    if(!hasEmployeeGetPermission) return;

    setLoading(true);
    try {
      let emp = await GetEmployees(0, navigate);
      //let employees = emp || [];
      const filteredDataNew = filterData(emp, keysToKeep);
      setData(filteredDataNew);
      setAllEmployees(emp);
      
      setExportData(ExportFilter(emp,departments))
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }finally {
      setLoading(false); 
    }
  };

  const applyFilters = (filters) => {
    if(!hasEmployeeGetPermission) return;

    const filtered = AllEmployees.filter((employee) => {
      
      const matchesFullName =
      !filters.fullName ||
      `${employee.firstName || ""} ${employee.lastName || ""}`
        .toLowerCase()
        .includes(filters.fullName.toLowerCase().trim());

      const matchesJobTitle =
        !filters.jobTitle || employee.jobTitle === filters.jobTitle;

      const matchesEmployeeRole =
        !filters.employeeRole || employee.position === filters.employeeRole;

      const matchesEmployeeType =
        !filters.employeeType || employee.empType === filters.employeeType;

      const matchesPhoneNumber =
        !filters.phoneNumber ||
        (employee.phoneNumbers &&
          employee.phoneNumbers.some((phone) =>
            phone.phoneNo.includes(filters.phoneNumber)
          ));

      const matchesEmail =
        !filters.email || (employee.email && employee.email.includes(filters.email));

      const matchesAddress =
        !filters.address ||
        `${employee.address || ""} ${employee.city || ""} ${employee.stateProvince || ""}`
          .toLowerCase()
          .includes(filters.address.toLowerCase());

      const matchesEmploymentStatus =
        !filters.employmentStatus ||
        employee.employmentStatus === filters.employmentStatus;

      const matchesDepartment =
        !filters.department ||
        employee.departmentID === parseInt(filters.department, 10);

      const matchesHireDate =
        (!filters.hireDateFrom || new Date(employee.hireDate) >= new Date(filters.hireDateFrom)) &&
        (!filters.hireDateTo || new Date(employee.hireDate) <= new Date(filters.hireDateTo));

      const matchesTags =
        !filters.tags.length ||
        (employee.tags &&
          filters.tags.every((tag) => employee.tags.includes(tag)));

      const matchesWageType =
        !filters.wageType || employee.wageType === filters.wageType;

      const matchesHourlyRate =
        !filters.hourlyRate || employee.wageAmount === parseFloat(filters.hourlyRate);

      const matchesAnnualPay =
        !filters.annualPay || employee.wageAmount === parseFloat(filters.annualPay);

      const matchesMinHoursWorked =
        !filters.minHoursWorked || employee.weeklyHours >= parseFloat(filters.minHoursWorked);

      const matchesMaxHoursWorked =
        !filters.maxHoursWorked || employee.weeklyHours <= parseFloat(filters.maxHoursWorked);

      const matchesAssignedTo =
        !filters.assignedTo || employee.referredBy === filters.assignedTo;

      // Combine all conditions
      return (
        matchesFullName &&
        matchesJobTitle &&
        matchesEmployeeRole &&
        matchesEmployeeType &&
        matchesPhoneNumber &&
        matchesEmail &&
        matchesAddress &&
        matchesEmploymentStatus &&
        matchesDepartment &&
        matchesHireDate &&
        matchesTags &&
        matchesWageType &&
        matchesHourlyRate &&
        matchesAnnualPay &&
        matchesMinHoursWorked &&
        matchesMaxHoursWorked &&
        matchesAssignedTo
      );
    });
    setFilteredData(filtered);
    setIsFiltered(true);

  };

  const handleReset = () => {
    setFilteredData(AllEmployees); // Reset to show all employees
    setIsFiltered(false);
  };

  const fetchDepartments = async () => {
    if(!hasEmployeeGetDepartmentsPermission) return;

    try {
      let dep = await GetDepartments(navigate);
      setDepartments(dep);    
    } catch (error) {
      console.error("Error fetching Departments data:", error);
    }
  }; 

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    maxSize: 2000000, // 2 MB in bytes
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    onDrop: acceptedFiles => {
      setBtnLoader(true);  
      setTimeout(() => {
        setEmpFiles(acceptedFiles.map(file => Object.assign(file)));
        setBtnLoader(false); 
      }, 1000);  // Simulating a delay for loading effect
    },
    onDropRejected: () => {
      toast.error('You can only upload 1 file with a maximum size of 2 MB.', {
       duration: 2000,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      //setMessage('You can only upload 1 file with a maximum size of 2 MB.');
      setBtnLoader(false);
    }
  });

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}-${day}-${year}`;
  };


  const getEmployeeByEmail = (data) => {      
    const empl = data.find(emp => emp.email === getUserName());    
    return empl ? empl : null;
  };
  const getEmployeeById = (id) => {      
    const empl = AllEmployees.find(emp => emp.employeeID === id);    
    return empl ? empl : null;
  };

  // const handlePaginationModel = (model) =>{
  //   if(model.pageSize === -1){
  //     setPaginationModel({
  //       page: 0,
  //       pageSize: -1,
  //     })
  //   }else{
  //     setPaginationModel(model);
  //   }  
  // }

  const handlePaginationModel = (model) => {
    const totalRows = filteredRows.length;
    let from = 1;
    let to = totalRows;

    if (model.pageSize !== -1) {
      from = model.page * model.pageSize + 1;
      to = Math.min(from + model.pageSize - 1, totalRows);
    }

    if(model.pageSize === -1){
          setPaginationModel({
            page: 0,
            pageSize: -1,
          })
        }else{
          setPaginationModel(model);
        }  
    setPaginationText(`${from}-${to} of ${totalRows}`);
  };

  const handleCSVExport = () => {
    if(!hasEmployeeGetPermission) return;

    if(exportToCSV(exportData, `Employees [${getCurrentDate()}].csv`, { delimiter: ',', includeHeaders: true })){
      Swal.fire(
            {
              title: 'Exported!',
              text: 'Data has been successfully exported!',
              icon: 'success',
              confirmButtonColor: '#23a26d', 
                customClass: {
            popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        },
            });
    }else{
      Swal.fire(
        {
          title: 'Failed!',
          text: 'No data available to export!',
          icon: 'error',
          confirmButtonColor: '#23a26d', 
            customClass: {
            popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        },
        });
    }
    
  };

  const handleExcelExport = () =>{   
    if(!hasEmployeeGetPermission) return;

    if(ExportToExcel(exportData, `Employees [${getCurrentDate()}]`)){
      Swal.fire(
        {
          title: 'Exported!',
          text: 'Data has been successfully exported!',
          icon: 'success',
          confirmButtonColor: '#23a26d', 
            customClass: {
           popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        },
        });
      }else{
        Swal.fire(
          {
            title: 'Failed!',
            text: 'No data available to export!',
            icon: 'error',
            confirmButtonColor: '#23a26d', 
              customClass: {
           popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        },
          });
      }
  }

  const handlePDFExport = () => {
    if(!hasEmployeeGetPermission) return;

    if(ExportToPdf(exportData, `Employees [${getCurrentDate()}]`)){
      Swal.fire(
            {
              title: 'Exported!',
              text: 'Data has been successfully exported!',
              icon: 'success',
              confirmButtonColor: '#23a26d', 
                customClass: {
          popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        },
            });
    }else{
      Swal.fire(
        {
          title: 'Failed!',
          text: 'No data available to export!',
          icon: 'error',
          confirmButtonColor: '#23a26d',
            customClass: {
           popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          htmlContainer: "custom-swal-html",
          confirmButton: "custom-swal-confirm",
        }, 
        });
    }
    
  };

  useEffect(() => {
    if (isEditing && employee) {        
        SaveEmp();
        setIsEditing(false);  
    }
}, [employee, isEditing]); 

const [Tag, setTag]  = useState('');

const handleOpenTagManagementDlg = (tags, id) => {
  
  setEmployee((prev) => {
    return { ...prev, employeeID: id, tags: tags };
  })
  setOpenTagManagementDlg(true);
}

const handleAddTag = async (tagName) => {
  if (tagName.trim() === '') return;

  const lowerCaseTagName = tagName.toLowerCase();

  const tagExists = employee.tags?.some(
    (tag) => tag.tagName.toLowerCase() === lowerCaseTagName
  );

  if (tagExists) return;

  const updatedTags = [...(employee.tags || []), { tagName }];
  const updatedEmployee = {
    ...employee,
    tags: updatedTags,
  };

  setEmployee(updatedEmployee);
  setTag('');

  try {
    await SaveEmployee({ employeeID: employee.employeeID, tags: updatedTags });
    await fetchData();
  } catch (error) {
    console.error('Error saving tag:', error);
  }
};

const handleDeleteTag = async (tagName) => {
  const updatedTags = employee.tags.filter(tag => tag.tagName !== tagName);

  const updatedEmployee = {
    ...employee,
    tags: updatedTags,
  };

  setEmployee(updatedEmployee);

  try {
    await SaveEmployee({
      employeeID: employee.employeeID,
      tags: updatedTags,
    });

    await fetchData();
  } catch (error) {
    console.error('Error deleting tag:', error);
  }
};

const handleDeleteTagByID = async (tagName, id) => {
  const updatedTags = employee.tags.filter(tag => tag.tagName !== tagName);

  const updatedEmployee = {
    ...employee,
    tags: updatedTags,
  };

  setEmployee(updatedEmployee);

  try {
    await SaveEmployee({
      employeeID: id,
      tags: updatedTags,
    });

    await fetchData();
  } catch (error) {
    console.error('Error deleting tag by ID:', error);
  }
};

  const columns = [
    {
      flex: 0.1,
      field: "id",
      minWidth: 50,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id } = row;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%"  , cursor:'pointer' , '&:hover' : {
           textDecoration:'underline'
          } 
          }} onClick={ (e) => {handleViewEmployee(e, row.id)}}>
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
   {    
  flex: 0.1,
  field: "Fullname",
  minWidth: 160,
  headerName: "Full Name",
  sortable: true, 
  sortingOrder: ['asc', 'desc'], 
  renderCell: ({ row }) => {
    const { Fullname } = row;
    return (
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          height: "100%", 
          cursor:'pointer', 
          '&:hover': {
            textDecoration:'underline'
          } 
        }}
        onClick={ (e) => {handleViewEmployee(e, row.id)}}
      >            
        <Typography sx={{ flexGrow: 1 }}>{Fullname || '-'} </Typography>
      </Box>
    );
  },
},
    {    
      flex: 0.1,
      minWidth: 145,
      field: "empType",
      headerName: "Type",
      editable: !hasEmployeeUpdatePersonalDetailPermission ? true : false,
      type: 'singleSelect',
      valueOptions: [
        { label: 'Employee', value: 'Employees' },
        { label: 'Subcontractor', value: 'Sub Contractors' }
      ],      
      renderCell: ({ row }) => {      
        const { empType } = row;
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            {empType && empType.length > 0 ? (
            <CustomChip
              rounded
              size="small"
              skin="light"
              sx={{ 
                lineHeight: 1, 
                cursor: hasEmployeeUpdatePersonalDetailPermission ? 'pointer' : 'default',
                '&:hover' : {
                    transform: hasEmployeeUpdatePersonalDetailPermission ? 'scale(1.05, 1.05)' : 'scale(1, 1)'
                  } 
                }}
              color={empType === "Employees" ? "warning" : empType==='Sub Contractors' ? "info" : 'error'} //'success' // Use color names: primary, secondary, error, warning, info, success
              label={empType === "Employees" ? "Employee" : empType === "Sub Contractors"? "Subcontractor" : '-'}
              backgroundColor='dark'
            />
            ) : (
            <Typography variant="body2" color="textSecondary">
              N/A
            </Typography>
            )
          }
        </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "jobTitle",
      minWidth: 160,
      headerName: "Job Title",
      editable: hasEmployeeUpdatePersonalDetailPermission ? true : false,
      type: 'singleSelect',
      valueOptions:['CEO', 'Owner', 'Accountant','Crew leader','Foreman','Property manager','Accounts payable','On-site maintenance staff','Others'],
      renderCell: ({ row }) => {
        const { jobTitle } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: '100%' }}>
            <Typography 
              sx={{ flexGrow: 1,
                '&:hover' : {
                  transform: hasEmployeeUpdatePersonalDetailPermission ? 'scale(1.05, 1.05)' : 'none'
                }
               }}
               >{jobTitle || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "createDate",
      minWidth: 180,
      headerName: "Created On",
      renderCell: ({ row }) => {
        const { createDate } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", cursor: 'default' }}>
            <Typography sx={{ flexGrow: 1 }}>{createDate ? dayjs(createDate).format("MM/DD/YYYY hh:mm A") : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 180,
      field: "modifiedBy",
      headerName: "Last Activity",
      renderCell: ({ row }) => {
        const { lastActivity } = row;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", cursor: 'default' }}>
            <Typography sx={{ flexGrow: 1 }}>{lastActivity|| '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "referredBy",
      minWidth: 180,
      editable: hasEmployeeUpdatePersonalDetailPermission ? true : false,
      headerName: "Referred By",
      renderCell: ({ row }) => {
        const { referredBy } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", 
            '&:hover' : {
                  transform: hasEmployeeUpdatePersonalDetailPermission ? 'scale(1.05, 1.05)' : 'none'
                }
           }}>
            <Typography sx={{ flexGrow: 1 }}>{referredBy || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "email",
      minWidth: 120,
      editable: hasEmployeeUpdatePersonalDetailPermission ? true : false,
      headerName: "Email",
      renderCell: ({ row }) => {
        const { email } = row;
  
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{email || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "isActive",      
      headerName: "Status",
      editable: hasEmployeeUpdatePersonalDetailPermission ? true : false,
      type: 'singleSelect',
      valueOptions:['Active', 'Inactive'],
      renderCell: ({ row }) => {
        const { isActive } = row;     

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1, cursor: 'pointer',
              '&:hover' : {
                  transform: hasEmployeeUpdatePersonalDetailPermission ? 'scale(1.05, 1.05)' : 'none'
                }
             }}
            color={isActive ? "success" : "secondary"} //'success' // Use color names: primary, secondary, error, warning, info, success
            label={isActive ? "Active" : "Inactive"}
          />
        );
      }
    },
    {
      flex: 0.1,
      minWidth: 280,
      field: "tags",
      headerName: "Tags",
      renderCell: ({ row }) => {
        const { tags, id } = row;
  
        return (
        <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "nowrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            {hasEmployeeUpdatePersonalDetailPermission && (
              <CustomChip
                rounded
                label="Add"
                size="small"
                color="info"
                skin="light"
                sx={{ lineHeight: 1 }}
                onDelete={() => handleOpenTagManagementDlg(tags, id)}
                deleteIcon={<Icon icon="tabler:circle-plus" />}
              />
            )}

            {tags && tags.length > 0 ? (
              tags.length <= 2 ? (
                tags.map((tag, index) => (
                  <CustomChip
                    key={index}
                    rounded
                    label={tag.tagName}
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ lineHeight: 1 }}
                    {...(hasEmployeeUpdatePersonalDetailPermission && {
                      onDelete: () => handleDeleteTagByID(tag.tagName, id)
                    })}
                  />
                ))
              ) : (
                <>
                  <CustomChip
                    rounded
                    label={tags[0].tagName}
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ lineHeight: 1 }}
                    {...(hasEmployeeUpdatePersonalDetailPermission && {
                      onDelete: () => handleDeleteTagByID(tags[0].tagName, id)
                    })}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    ...
                  </Typography>
                  <CustomChip
                    rounded
                    label="See More"
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ 
                      lineHeight: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 58, 112, 0.16)',
                      },  
                    }}
                    onClick={() => handleOpenTagManagementDlg(tags, id)}
                  />
                </>
              )
            ) : (
              <Typography variant="body2" color="textSecondary">
                No tags
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  const showConfirmation = (id, Name) => {
    MySwal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#23A26D',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        // Perform delete action here
        handleDeleteEmployee(id,Name);        
      }
    });
  };

  const showBulkConfirmation = (count, message, type,BulkData, btnText) => {
     const confirmButtonText = type === 'Delete' 
    ? 'Yes, delete it' 
    : 'Yes, apply changes';
     MySwal.fire({
      title: '<strong>Are you sure?</strong>',
      html: message,
      showCancelButton: true,
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'No, keep it',
      width: '390px', // Explicit width control
      padding: '1rem',
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: '&times;', // This is the × symbol
      customClass: {
        popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        if(type === 'Delete'){
          handleBulkDelete(); 
        }else if(type === 'Operation'){
          handleBulkSave(BulkData);
        }
              
      }
    });
  };

  const SaveEmp = async () => {
    try {                  
      const result = await SaveEmployee(employee);
      if (result && !result.isError) {           
        setOpenNewCustomerDlg(false);
        setActiveStep(0); 
        fetchData();
        toast.success(`${modalData.successTitle} ${modalData.successTxt}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });    
      } else {
        toast.error(`Failed! ${result.result[0].failureReason}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });            
        console.error('Error saving employee:', result.message);            
      }
    } catch (error) {
      console.error('Error saving employee:', error);          
    }
  };

  const handleClearSearch = () =>  handleSearchChange('');

  const handleRowDataChange = async  ( newRow, originalRow) => { 

    if (_.isEqual(newRow, originalRow)) {
      return originalRow;
    }

    const updatedRow = { ...newRow, isNew: false };
    setData((prevData) => prevData.map((row) => (row.id === newRow.id ? updatedRow : row)));

    let firstName  = updatedRow.Fullname?.split(' ')[0] || '';
    let lastName = updatedRow.Fullname?.split(' ')[1] || '';

    let isActive = updatedRow.isActive === 'Active';

    let emp = getEmployeeById(updatedRow.id);

    emp = {
      ...emp, 
      firstName: firstName,
      lastName:lastName,  
      isActive:isActive, 
      empType:newRow.empType, 
      referredBy:newRow.referredBy,
      jobTitle:newRow.jobTitle,
      email:newRow.email
    };

    setEmployee((prevEmp) => ({ ...prevEmp, ...emp }));
    setIsEditing(true);

    setModalData((prevModalData) => ({
      ...prevModalData,  
      successTitle: 'Updated!', 
      successTxt: 'Employee Personal Information updated.'
    }));

    return updatedRow;
  };

  const handleProcessRowUpdateError = ()  => {

  }  

  const handleGridSelection = (sr) => {
    setSelectedRows(sr);
    // console.log(Object.values(sr).length,Object.values(sr) );
  };  

  const handleBulkEmpTypeChange = (value) =>{
    setBulkEmpType(value);
  }
  const handleBulkEmpStatusChange = (value) =>{
    setBulkEmpStatus(value);
  }

  const handleBulkOperations = async () =>{   
    let BulkData = [];
    let count = Object.values(selectedRows).length;
    if(count > 0 && (bulkEmpType || bulkEmpStatus)){
        for(var i = 0; i< Object.values(selectedRows).length; i++){
          BulkData.push(
            {
              "EmployeeID" : Object.values(selectedRows)[i],
              "empType" : bulkEmpType || null,
              "isActive" : bulkEmpStatus? bulkEmpStatus === 'Active' : null
            }
          )
        }
        showBulkConfirmation(count,`Do you want to make changes to ${count} ${count === 1? 'Employee' : 'Employees'}?`,'Operation',BulkData, 'Yes, do it');
      
        return;
    }else if (!bulkEmpType){
      toast.error(`Failed! Please select employee type before proceeding.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
    }
    else if (!bulkEmpStatus){
      toast.error(`Failed! Please select status before proceeding.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
    }    
  }


  const handleBulkSave = async (BulkData) =>{
    try {   
      NProgress.start();               
      const result = await SaveBulkEmployee(BulkData);
      if (result && !result.isError) {
        await fetchData()                           
        toast.success(`Updated! Employee Information updated successfully.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          }); 
          handleClearSelection();          
      } else {
        toast.error(`Failed! ${result.result[0].failureReason}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });     
          handleClearSelection();       
        console.error('Error saving employee:', result.message);        
      }
    } catch (error) {
      handleClearSelection();
      console.error('Error saving employee:', error);          
    }finally {
      NProgress.done(); 
    }
  }

  const handleBulkDelete = async () => {
    let BulkData = [];
    if(Object.values(selectedRows).length > 0){
      for(var i = 0; i< Object.values(selectedRows).length; i++){
        let EmpID = Object.values(selectedRows)[i];
        let Emp =  getEmployeeById(Object.values(selectedRows)[i]);
        handleDeleteEmployee(EmpID,Emp.firstName+' '+Emp.lastName);
      }
    }  
  }

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
    setBulkEmpStatus(0);
    setBulkEmpType(0);
  };

  const handleDeleteEmployee = async (empID, Name) =>{  
    NProgress.start();  
      try {
        const result = await DeleteEmployee(empID,navigate);
        if (result && result.form) {
          console.error(result.form);
        } else {
         await fetchData(); 
          toast.success(`Deleted! ${Name || 'Data'} has been deleted.`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
            });                 
        }
      } catch (error) {
        console.error('Error deleting employee:', error);      
      }finally {
        NProgress.done(); 
    }
  }

  const handleBack = () => {   
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleViewEmployee = (e, Id) => {
    e.preventDefault(); 
    if (e.ctrlKey){      
      window.open(`/employees/detail/${Id}`, '_blank')
    }else{
      navigate(`/employees/detail/${Id}`);
    }
  }

  const handleNext = async () => {
    if (validateFields(activeStep)) {
      NProgress.start();
        
      if (activeStep === steps.length - 1) {
        SaveEmp();
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
  
      NProgress.done();
    }
  };
  
  // const handleFilterClick = (buttonIndex, searchKeyWord) => {
  //   setSelectedButton(buttonIndex);    
  //   setSearchText(searchKeyWord);
  //   setPaginationModel((prevModel) => ({
  //     ...prevModel,
  //     page: 0,
  //   }));
  // };

  const handleSearchChange = (searchKeyWord) =>{
    setSearchText(searchKeyWord);      
  }

  const filteredRows = (isFiltered 
    ? filterData(filteredData, keysToKeep) 
    : data || [])
      .filter(row => {    
          if (filterType === 'Sub Contractors') {
              return row.empType === 'Sub Contractors';
          } else if (filterType === 'Employees') {
              return row.empType === 'Employees';
          }      
          return true;
      })
      .filter(row =>    
          Object.values(row).some(value =>
              value && value.toString().toLowerCase().includes(searchText.toLowerCase())
          )
      );

  const [paginationText, setPaginationText] = useState('');

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
  
    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }
  
    setPaginationText(`${start}-${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

const handleFilterTypeChange = (buttonIndex,type) => {
  setFilterType(type);  
  setSelectedButton(buttonIndex);        
  setPaginationModel((prevModel) => ({
      ...prevModel,
      page: 0,
    }));
};

  const handleCloseTagManagementDlg = () => {
    setOpenTagManagementDlg(false);
  }

  const handleOpenNewCustomerDlg = () => {
    setOpenNewCustomerDlg(true);
    setModalData({title:'Create Employee', btnTxt:'Create Employee', successTitle:'Created!', successTxt:'Employee has been successfully created.'})
    setActiveStep(0);
    resetEmployee();
  };

  const handleOpenBulkImportDlg = () => {
    setOpenBulkImportDlg(true);   
  };

  const handleEditEmployee = (id) => {
    resetEmployee();
    let emp = getEmployeeById(id);    
    setEmployee(emp);
    setModalData({title:'Edit Employee', btnTxt:'Save Changes', successTitle:'Updated!', successTxt:'Employee information successfully updated.'})
    setOpenNewCustomerDlg(true);
  }

  const handleCloseNewCustomerDlg = () => {
    setOpenNewCustomerDlg(false);
    resetEmployee();
    setErrors({});
  };

  const handleCloseBulkImportDlg = () => {
    setOpenBulkImportDlg(false);    
  };

  const handleAddPhoneField = () => {
    setPhoneFields([
      ...phoneFields,
      { id: phoneFields.length + 1, value: "", primary: false },
    ]);
  };

  const handlePhoneFieldChange = (id, event) => {
    const newPhoneFields = phoneFields.map((field) =>
      field.id === id ? { ...field, value: event.target.value } : field
    );
    setPhoneFields(newPhoneFields);
  };

  const handleDeletePhoneField = (id) => {
    setPhoneFields(phoneFields.filter((field) => field.id !== id));
  };

  const handleSetPrimary = (id) => {
    const newPhoneFields = phoneFields.map((field) =>
      field.id === id
        ? { ...field, primary: true }
        : { ...field, primary: false }
    );
    setPhoneFields(newPhoneFields);
  };

  const handleChange = (field, value) => {
    setEmployee((prevState) => ({ ...prevState, [field]: value }));    
  };


  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fragment>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Profile Information
              </Typography>
            </Box>
            <Grid container spacing={5} sx={{mb: 6}}>
              <Grid item xs={12} sm={12}>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 6 },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                >General Information</Divider>
              </Grid>              
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  label="First Name"
                  placeholder="First Name"
                  value={employee.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}  
                  error={!!errors.firstName} 
                  helperText={errors.firstName}          
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  label="Last Name"
                  placeholder="Last Name"
                  value={employee?.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  error={!!errors.lastName} 
                  helperText={errors.lastName} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                  <CustomTextField
                  fullWidth
                  label="Email"
                  placeholder='name@example.com'
                  value={employee.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={!!errors.email} 
                  helperText={errors.email}
                  />
                </Grid>   
                <Grid item xs={12} sm={6}>
                <CustomTextField
                    label='Employee Type'
                  fullWidth
                  select
                  value={employee?.empType? employee?.empType : 'select'}
                  onChange={(e) => handleChange('empType', e.target.value)}
                  error={!!errors.empType} 
                  helperText={errors.empType}
                >
                    <MenuItem value='select'>Select</MenuItem>
                    <MenuItem value='Employees'>Employee</MenuItem>
                    <MenuItem value='Sub Contractors'>Subcontractor</MenuItem>
                </CustomTextField>
              </Grid>
                <Grid item xs={12} sm={6}>
                <CustomTextField
                    label='Employee Role'
                  fullWidth
                  select   
                  defaultValue="select"               
                  value={employee?.position? employee?.position : 'select'}
                  onChange={(e) => handleChange('position', e.target.value)}
                >
                    <MenuItem value='select'>Select</MenuItem>                  
                    <MenuItem value='Admin'>Owner</MenuItem>
                    <MenuItem value='Manager'>Admin</MenuItem>
                    <MenuItem value='Employee'>Manager</MenuItem>
                    <MenuItem value='Customer'>Crew</MenuItem>
                </CustomTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  label="Date of Birth"
                  type='date'
                  value={employee?.dateOfBirth? getDate(employee.dateOfBirth): ''}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  error={!!errors.dateOfBirth} 
                  helperText={errors.dateOfBirth}
                />
              </Grid>
              <Grid item xs={12} sm={6}>               
                  <CustomTextField
                    label='Referred By'
                    fullWidth                    
                    value={employee.referredBy}
                    onChange={(e) => handleChange('referredBy', e.target.value)}                                 
                  >      
                  </CustomTextField>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Grid item xs={7.6}>
                    {phoneFields.map((field) => (
                      <Box
                        key={field.id}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CustomTextField
                          fullWidth
                          type='tel'
                          placeholder="(000) 000-000"
                          label={`Phone # ${field.id}`}
                          value={employee.phoneNumber}
                          onChange={(e) => handleChange('phoneNumber', e.target.value)}
                          sx={{ mb: 2 }}
                          error={!!errors.phoneNumber} 
                          helperText={errors.phoneNumber}
                        />
                        <IconButton
                          color="secondary"
                          onClick={() => handleSetPrimary(field.id)}
                          disabled={field.primary}
                        >
                          {field.primary ? (
                            <StarIcon sx={{ color: "#E7D557" }} />
                          ) : (
                            <StarOutlineIcon />
                          )}
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDeletePhoneField(field.id)}
                          disabled={field.primary}
                        >
                          <Icon icon="tabler:trash" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleAddPhoneField}
                    >
                      Add Phone
                    </Button>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 6 },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                >Address</Divider>
              </Grid>
              <Grid item xs={12} sm={10}>
                <CustomTextField
                  fullWidth
                  placeholder="Street 1"
                  label="Main Address"
                  value={employee.address} 
                  onChange={(e) => handleChange('address', e.target.value)} 
                  error={!!errors.address} 
                  helperText={errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={10}>
                <CustomTextField fullWidth placeholder="Street 2"  
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="Floor/Level" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="Unit Number" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="City" value={employee.city} onChange={(e) => handleChange('city', e.target.value)} error={!!errors.city}helperText={errors.city} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="State" value={employee.stateProvince} onChange={(e) => handleChange('stateProvince', e.target.value)} error={!!errors.stateProvince}helperText={errors.stateProvince} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="Zip code" value={employee.postalCode} onChange={(e) => handleChange('postalCode', e.target.value)} error={!!errors.postalCode}helperText={errors.postalCode}  />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField fullWidth placeholder="County" value={employee.country} onChange={(e) => handleChange('country', e.target.value)} error={!!errors.country}helperText={errors.country} />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Divider
                  sx={{
                    color: "text.disabled",
                    "& .MuiDivider-wrapper": { px: 6 },
                    fontSize: theme.typography.body2.fontSize,
                    my: (theme) => `${theme.spacing(3)} !important`,
                  }}
                >Emergency Contact</Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                    fullWidth
                    label='Full Name'
                    value={employee.emergencyContactName}
                    onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                    error={!!errors.emergencyContactName} 
                    helperText={errors.emergencyContactName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                    fullWidth
                    label='Relation'
                    value={employee.emergencyContactRelationship}
                    onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                    error={!!errors.emergencyContactRelationship} 
                    helperText={errors.emergencyContactRelationship}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField 
                    fullWidth
                    label='Phone'
                    placeholder='(000) 000-000'
                    value={employee.emergencyContactPhone}
                    onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                    error={!!errors.emergencyContactPhone} 
                    helperText={errors.emergencyContactPhone}
                />
              </Grid>
            </Grid>
          </Fragment>
        );
      case 1:
        return (
          <Fragment key={step}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Employment Details
              </Typography>
            </Box>
            <Grid container spacing={5} sx={{mb: 6}}>
                <Grid item xs={12}>
                    <Divider
                    sx={{
                        color: "text.disabled",
                        "& .MuiDivider-wrapper": { px: 6 },
                        fontSize: theme.typography.body2.fontSize,
                        my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                    >General Information</Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        select
                        label='Departments'
                        defaultValue={employee?.departmentID || 'select'}
                        onChange={(e) => handleChange('departmentID', e.target.value)}
                        error={!!errors.departmentID} 
                        helperText={errors.departmentID}
                    >
                        <MenuItem disabled value='select'>Select</MenuItem>
                        {departments? departments.map((dept) => (
                          <MenuItem key={dept.key} value={dept.key}>
                            {dept.value}
                          </MenuItem>
                        )) : {}}
                    </CustomTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        select
                        label='Job Title'
                        defaultValue={employee?.jobTitle || 'select'}
                        onChange={(e) => handleChange('jobTitle', e.target.value)}
                        error={!!errors.jobTitle} 
                        helperText={errors.jobTitle}>
                      <MenuItem disabled value='select'>Select</MenuItem>
                      <MenuItem disabled value='CEO'>CEO</MenuItem>
                      <MenuItem disabled value='Owner'>Owner</MenuItem>
                      <MenuItem disabled value='Accountant'>Accountant</MenuItem>
                      <MenuItem disabled value='Crew leader'>Crew leader</MenuItem>
                      <MenuItem disabled value='Foreman'>Foreman</MenuItem>
                      <MenuItem disabled value='Property manager'>Property manager</MenuItem>
                      <MenuItem disabled value='Accounts payable'>Accounts payable</MenuItem>
                      <MenuItem disabled value='On-site maintenance staff'>On-site maintenance staff</MenuItem>
                      <MenuItem disabled value='Others'>Others</MenuItem>
                    </CustomTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        select
                        label='Employment Status'
                        defaultValue={employee?.employmentStatus? employee?.employmentStatus : 'select' }
                        onChange={(e) => handleChange('employmentStatus', e.target.value)}
                        error={!!errors.employmentStatus} 
                        helperText={errors.employmentStatus}
                    >
                        <MenuItem value='select'>Select</MenuItem>
                        <MenuItem value='FullTime'>Full Time</MenuItem>
                        <MenuItem value='PartTime'>Part Time</MenuItem>
                        <MenuItem value='Contract'>Contract</MenuItem>
                    </CustomTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        label='Hire Date'
                        type='date'
                        value={employee?.hireDate? getDate(employee.hireDate) : ''}
                        onChange={(e) => handleChange('hireDate', e.target.value)}
                        error={!!errors.hireDate} 
                        helperText={errors.hireDate}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        label='Probation Date'
                        type='date'
                        value={employee?.probationEndDate? getDate(employee.probationEndDate) : ''}
                        onChange={(e) => handleChange('probationEndDate', e.target.value)}
                        error={!!errors.probationEndDate} 
                        helperText={errors.probationEndDate}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        label='Driving License Expiry'
                        type='date'
                        value={employee?.drivingLicenseExpiryDate? getDate(employee.drivingLicenseExpiryDate) : ''}
                        onChange={(e) => handleChange('drivingLicenseExpiryDate', e.target.value)}
                        error={!!errors.drivingLicenseExpiryDate} 
                        helperText={errors.drivingLicenseExpiryDate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Divider
                    sx={{
                        color: "text.disabled",
                        "& .MuiDivider-wrapper": { px: 6 },
                        fontSize: theme.typography.body2.fontSize,
                        my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                    >Employee Wage Details</Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        select
                        label='Wage Type'
                        type='date'
                        defaultValue={employee?.wageType || 'select'}
                        onChange={(e) => handleChange('wageType', e.target.value)}
                        error={!!errors.wageType} 
                        helperText={errors.wageType}
                    >
                        <MenuItem value='select'>Select</MenuItem>
                        <MenuItem value='Hourly'>Hourly</MenuItem>
                        <MenuItem value='Salary'>Salary</MenuItem>
                    </CustomTextField>
                </Grid>
                {employee?.wageType === 'Salary' && (
                  <>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      label="Annual Salary"
                        InputProps={{
                                     startAdornment: (
                                       <InputAdornment position="start" sx={{ fontSize: '1.32rem' }}>$</InputAdornment>
                                     ),
                                   }}
                      type='number'
                      value={employee?.wageAmount}
                      onChange={(e) => handleChange('wageAmount', e.target.value)}
                      error={!!errors.departmentID}
                      helperText={errors.departmentID}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                          <CustomTextField
                            fullWidth
                            label="Weekly Hours"
                            type="number"
                            value={employee?.weeklyHours ?? ""}
                            onChange={(e) => {
                              const input = e.target.value;
                              // Allow empty during editing
                              if (input === "") {
                                handleChange("weeklyHours", "");
                                return;
                              }
                              // Validate number input
                              const numValue = Number(input);
                              if (!isNaN(numValue)) {
                                const clampedValue = Math.min(
                                  Math.max(numValue, 0),
                                  168
                                );
                                handleChange("weeklyHours", clampedValue);
                              }
                            }}
                            onBlur={(e) => {
                              // When field loses focus, enforce default if empty
                              if (
                                e.target.value === "" ||
                                isNaN(Number(e.target.value))
                              ) {
                                handleChange("weeklyHours", 40);
                              }
                            }}
                            inputProps={{
                              max: 168,
                              min: 0,
                              step: 1,
                            }}
                          />
                        </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                        fullWidth
                        disabled
                        label='Hourly Rate'
                        value={employee?.wageAmount ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }).format(parseFloat(employee.wageAmount/(employee?.weeklyHours? employee?.weeklyHours*52 : 40*52))) : '$0.00'}
                        onChange={(e) => handleChange('wageAmount', e.target.value)}
                    />
                  </Grid>
                  </>
                )}
                {employee?.wageType === 'Hourly' && (
                  <Grid item xs={12} sm={6}>
                  <CustomTextField
                      fullWidth
                      type='number'
                      label='Hourly Rate'
                       InputProps={{
                                                           startAdornment: (
                                                             <InputAdornment position="start" sx={{ fontSize: '1.32rem' }}>$</InputAdornment>
                                                           ),
                                                         }}
                          value={employee?.wageAmount}
                          onChange={(e) => handleChange('wageAmount', e.target.value)}
                      />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                    <FormControlLabel
                        label={
                        <Typography sx={{fontWeight: 'bold'}}>
                            Overtime Exempt
                        </Typography>
                        }
                        control={<Checkbox name="basic-unchecked" />}
                    />
                    <Typography variant="body2" color="textSecondary">
                        Your employees are not exempt from overtime rules.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Divider
                    sx={{
                        color: "text.disabled",
                        "& .MuiDivider-wrapper": { px: 6 },
                        fontSize: theme.typography.body2.fontSize,
                        my: (theme) => `${theme.spacing(3)} !important`,
                    }}
                    >Employee Availability</Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  select
                  label='Working Days'
                  value={'select'}
                  defaultValue='select'                  
                >
                  <MenuItem value='select' disabled>
                    Select
                  </MenuItem>                                
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                    <MenuItem key={day} value={index}>
                       {day} 
                    </MenuItem>
                  ))}
                </CustomTextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                <FormControl>
                  <FormLabel id="clockInRequired">
                    Clock in/out required
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="clockInRequired"
                    name="clockInRequiredSelection"                   
                    value={employee.clockInOutRequired ? 'yes' : 'no'}
                    onChange={(e) => handleChange('clockInOutRequired', e.target.value === 'yes')}
                  > 
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                        <Typography>
                          Working Hours
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CustomTextField
                          fullWidth
                          required
                          type="time"
                          label="From"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CustomTextField
                          fullWidth
                          required
                          type="time"
                          label="To"
                        />
                      </Grid>
            </Grid>
          </Fragment>
        );
      case 2:
        return (
          <Fragment key={step}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Attachments
              </Typography>
            </Box>
            <Grid container spacing={5}>
                <Grid item xs={12}>
                    <FileUploader />
                </Grid>
            </Grid>
          </Fragment>
        );
      default:
        return "Unknown Step";
    }
  };  

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const handleNewEmployee = () => {
    navigate('/employees/create');
  };
  

  const validateFields = (step) => {
    let tempErrors = {};
    if(step === 0){
      if (!employee.firstName) tempErrors.firstName = 'First Name is required';
      if (!employee.lastName) tempErrors.lastName = 'Last Name is required';
      if (!employee.email ) tempErrors.email = 'Email is required';
      if (employee.email && !validateEmail(employee.email)) tempErrors.email = 'Enter a vaild Email';
      if (!employee.empType || employee.empType === 'select') tempErrors.empType = 'Type is required';    
     // if (!employee.dateOfBirth || employee.dateOfBirth === '' ) tempErrors.dateOfBirth = 'Date of Birth is required';
      // if (!employee.referredBy ) tempErrors.referredBy = 'Referred By is required';
      if (!employee.address ) tempErrors.address = 'Address is required';
      //if (!employee.city ) tempErrors.city = 'City is required';
      //if (!employee.stateProvince ) tempErrors.stateProvince = 'State is required';
      //if (!employee.postalCode ) tempErrors.postalCode = 'Zip Code is required';
      //if (!employee.country ) tempErrors.country = 'Country is required';
    }
    if(step === 1){
      if (!employee.departmentID) tempErrors.departmentID = 'Department is required';
      if (!employee.jobTitle) tempErrors.jobTitle = 'Job Title is required';
      if (!employee.employmentStatus) tempErrors.employmentStatus = 'Employee Status is required';
      if (!employee.hireDate || employee.hireDate === '') tempErrors.hireDate = 'Hire Date is required';
      //if (!employee.probationEndDate || employee.probationEndDate === '') tempErrors.probationEndDate = 'Probation Date is required';
      if (!employee.drivingLicenseExpiryDate || employee.drivingLicenseExpiryDate === '') tempErrors.drivingLicenseExpiryDate = 'Driving License Expiry is required';    
      if (!employee.wageType ) tempErrors.wageType = 'Wage Type is required';
      if (!employee.wageAmount ) tempErrors.wageAmount = 'Annual Salary is required';
    }
    
    setErrors(tempErrors);
    
    // Return true if no errors
    return Object.keys(tempErrors).length === 0;
  };

  const renderContent = () => {
    return (
        <form onSubmit={(e) => e.preventDefault()}>
          <Grid container spacing={5}>
            {getStepContent(activeStep)}
          </Grid>
        </form>
    )
  };

  // if (loading) {
  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         height: '100vh',
  //       }}
  //     >
  //       <CircularProgress />
  //       <Typography sx={{ ml: 2 }}>Loading...</Typography>
  //     </Box>
  //   );
  // }

  const buttons = [
    { name: 'All', variant: 'contained', color: 'btn.primary' , searchKeyWord : '' },
    { name: 'Employee', variant: 'text', color: 'secondary', searchKeyWord : 'Employees' },
    { name: 'Subcontractors', variant: 'text', color: 'secondary' ,searchKeyWord : 'Sub Contractors'},
  ];


  const NoRowsOverlay = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: 2,
        }}
      >
        <Typography variant="h6" color="secondary">
          Oops! No Data Found
        </Typography>
      </Box>
    );
  };
  
  const [openEmployeeDlg, setOpenEmployeeDlg] = useState(false);

  const handleOpenEmployeeDlg = () => setOpenEmployeeDlg(true);
  const handleCloseEmployeeDlg = () => setOpenEmployeeDlg(false); 

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Employees"}
    >
      <Card>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                pointerEvents: !hasEmployeeGetPermission ? "none" : "default",
                width: "100%",
              }}
            >
              <Box sx={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: { xs: 1, sm: 2 },
                alignItems: "center"
              }}>
                {buttons.map((button, index) => (
                  <Button
                    key={index}
                    color={selectedButton === index ? "main" : "secondary"}
                    variant={selectedButton === index ? "contained" : "tonal"}
                    onClick={() => handleFilterTypeChange(index, button.searchKeyWord)}
                    sx={{
                      minWidth: { xs: '30%', sm: 'auto' },
                      flex: { xs: '1 1 auto', sm: '0 1 auto' }
                    }}
                  >
                    {button.name}
                  </Button>
                ))}
              </Box>
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  mx: { xs: 0, sm: 4 },
                  display: { xs: 'none', sm: 'block' } 
                }} 
              />
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: { xs: 2, sm: 4 },
                  py: 2,
                  width: { xs: "100%", sm: "auto" },
                  textAlign: { xs: "center", sm: "left" },
                  mt: { xs: 2, sm: 0 },
                }}
              >
                <Typography variant="body2" sx={{ pointerEvents: "none", fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          titleTypographyProps={{ sx: { mb: [1, 0] }, width: '100%' }}
          action={
            <Box sx={{ 
              display: "flex", 
              gap: { xs: 2, sm: 3 }, 
              flexWrap: "wrap",
              width: { xs: '100%', sm: 'auto' },
              mt: { xs: 1, sm: 0 },
              justifyContent: { xs: 'space-between', sm: 'flex-end' }
            }}>
              <CustomTextField
                value={searchText}
                placeholder="Search"
                disabled={!hasEmployeeSearchPermission}
                {...(hasEmployeeSearchPermission && {
                  onChange: (e) => handleSearchChange(e.target.value),
                })}
                size="small"
                sx={{
                  width: { xs: '100%', sm: '200px', md: '250px' },
                  '& .MuiInputBase-root': {
                    width: { xs: '100%', sm: 'auto' },
                  },
                  order: { xs: 1, sm: 1 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 0 }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <AdvancedFilter
                onApplyFilters={applyFilters}
                handleReset={handleReset}
                departments={departments}
                filtered={filteredData}
                hasEmployeeSearchPermission={hasEmployeeSearchPermission}
                sx={{
                  order: { xs: 3, sm: 2 }
                }}
              />
              <Button
                onClick={handleOpenEmployeeDlg}
                disabled={!hasEmployeeSavePermission}
                variant="contained"
                color="main"
                startIcon={<AddIcon />}
                sx={{
                  order: { xs: 2, sm: 3 },
                  flex: { xs: '1 1 auto', sm: '0 1 auto' }
                }}
              >
                  New Employee
              </Button>
              <OptionsMenu
                iconButtonProps={{
                  size: "small",
                  sx: { 
                    color: "text.secondary",
                    order: { xs: 4, sm: 4 }
                  },
                }}
                options={[
                  {
                    text: "Import",
                    icon: <UploadIcon fontSize="small" sx={{ mr: 1 }}/>,
                    menuItemProps: {
                      ...(hasEmployeeSavePermission && {
                        onClick: () => handleOpenBulkImportDlg(),
                      }),
                    },
                  },
                  {
                    text: "Export",
                    icon: <DownloadIcon fontSize="small" sx={{ mr: 1 }}/>,
                    menuItemProps: {
                      ...(hasEmployeeGetPermission && {
                        onClick: () => {
                         Swal.fire({
                          title: `<strong style="color: ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68)' : theme.palette.text.primary};">Export</strong>`,
                          html: `
                          <style>
                            .swal2-popup{
                              background-color: ${theme.palette.mode === 'dark' ? '#1B2431' : '#fff'};
                            }
                            .exportStyle{
                              display: flex; 
                              align-items: center; 
                              justify-content: center;
                              gap: 20px;
                              height: 150px;
                              margin-top: 15px; /* Added for consistent spacing */
                            }       
                            .custom-close-btn {
                                position: absolute;
                                right: 10px;
                                top: 12px;
                                width: 30px;
                                height: 30px;
                                cursor: pointer;
                                z-index: 1000;
                                color: #555;
                                font-size: 30px;
                                line-height: 38px;
                                text-align: center;
                                background: none;
                                border: none;
                                padding: 0;
                                margin: 0;
                            }
                            .custom-close-btn:hover {
                                color: ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68)' : '#333'};
                            }    
                            .subtitle{
                              color: ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68)' : theme.palette.text.primary};
                            }                   
                            .iconBorder{
                                  border: 1px solid transparent; /* Made border transparent */
                                border-radius: 8px;
                                padding: 15px;
                                cursor: pointer;
                                transition: all 0.3s ease;
                                margin-top: 15px; /* Adjusted for consistent spacing */
                                position: relative;
                                width: 130px;
                                height: 145px; /* Set to 140px as requested */
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                                  box-shadow: 
                                0 0 0 1px rgba(0, 0, 0, 0.12), /* Black border effect at 12% opacity */
                                0 0 20px #10182808; /* Original shadow */
                            }
                            .selected{
                                border: 2px solid #23a26d !important;
                                background-color: ${theme.palette.mode === 'dark' ? '#1B2431 !important' : '#f5fff9 !important'};
                            }
                            .selected::after {
                                content: "";
                                position: absolute;
                                top: 10px;
                                right: 10px;
                                width: 16px;
                                height: 16px;
                                border-radius: 50%;
                                background-color: #23a26d;
                                border: 3px solid white;
                                box-shadow: 0 0 0 1px #23a26d;
                            }
                            .descriptionText{
                                text-align:left;
                                margin-bottom: 25px;
                                margin-top: 20px;
                                color: #555;
                            }
                            .swal2-html-container {
                                padding: 0em 1.6em 1.3em !important;
                            }
                            .exportIcons{
                                margin-bottom: 10px;
                            }
                            .format-label {
                                font-size: 14px;
                                color: ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68) !important' : '#333 !important'};
                                margin-top: 8px;
                            }
                            .swal2-close {
                                color: #000 !important;
                                font-size: 20px !important;
                                width: 24px !important;
                                height: 34px !important;
                            }
                            .swal2-close:hover {
                                color: #000 !important;
                            }
                            .swal2-cancel{
                                background: transparent;
                                color: #313D4F;
                                border: 1px solid #313D4F;
                            }
                            .swal2-cancel:hover{
                                background-color: ${theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.68) !important' : 'rgba(49, 61, 79, 0.08) !important'};
                                border: 1px solid #313D4F;
                                background-image: none;
                            }
                            .swal2-confirm{
                                background-color: #23a26d;
                            }
                            .swal2-confirm:hover{
                                background-color: #1d8a5a !important;
                            }
                              
                          </style>
                          <div>
                            <button class="custom-close-btn" id="customCloseBtn">×</button>
                            <p class="subtitle">Choose your preferred file format to export your data</p>
                            <div class="exportStyle">                            
                              <div class="iconBorder" id="CSV-Container">
                                <img src="./images/csv.png" class="exportIcons" id="exportCsv" width="50" height="50">
                                <span class="format-label">CSV File</span>                             
                              </div>  
                              <div class="iconBorder" id="XLSX-Container">
                                <img src="./images/xlsx.png" class="exportIcons" id="exportXlsx" width="50" height="50">
                                <span class="format-label">XLSX File</span>
                              </div>                                                            
                            </div>
                          <div>                          
                          `,
                          showCancelButton: true,
                          confirmButtonText: 'Export',
                          cancelButtonText: 'Cancel',
                          confirmButtonColor: "#23a26d",
                          customClass: {
                            popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
                            title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
                            htmlContainer: 'custom-swal-html',
                            confirmButton: 'custom-swal-confirm',
                            cancelButton: 'custom-swal-cancel',
                            closeButton: 'custom-swal-close'
                          },
                          showCloseButton: false,
                          width: "620px",
                          didOpen: () => {
                            document
                              .getElementById("customCloseBtn")
                              .addEventListener("click", () => {
                                Swal.close();
                              });
                            let ExportButton =
                              document.getElementsByClassName(
                                "swal2-confirm"
                              )[0];
                            if (ExportButton) {
                              ExportButton.disabled = true;
                            }

                            const selectFormat = (format) => {
                              document
                                .getElementById("CSV-Container")
                                .classList.remove("selected");
                              document
                                .getElementById("XLSX-Container")
                                .classList.remove("selected");
                              document
                                .getElementById(`${format}-Container`)
                                .classList.add("selected");
                              ExportButton.disabled = false;
                            };

                            document
                              .getElementById("CSV-Container")
                              .addEventListener("click", () =>
                                selectFormat("CSV")
                              );
                            document
                              .getElementById("XLSX-Container")
                              .addEventListener("click", () =>
                                selectFormat("XLSX")
                              );
                          },
                         }).then((result) => {
                           if (result.isConfirmed) {
                             if (
                               document
                                 .getElementById("XLSX-Container")
                                 .classList.contains("selected")
                             ) {
                               handleExcelExport();
                             } else {
                               handleCSVExport();
                             }
                           }
                         });
                        },
                      }),
                    },
                  },
                ]}
              />
            </Box>
          }
          sx={{
            py: { xs: 2, sm: 4 },
            flexDirection: { xs: "column", sm: "row" },
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: { xs: 2, sm: 0 }
          }}
        />
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              alignItems: { xs: "flex-start", sm: "center" },
              mx: { xs: 2, sm: 6 },
              mt: 2,
              mb: 4,
              px: { xs: 4, sm: 0 }
            }}
          >
            {smBelow && (
              <Divider />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: { xs: 2, sm: 4 },
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 1 }
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                {selectedRows.length} {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
              <CustomTextField
                select
                sx={{
                  minWidth: { xs: '100%', sm: '120px' },
                  '& .MuiInputBase-root': {
                    width: { xs: '100%', sm: 'auto' },
                  },
                  order: { xs: 2, sm: 2 }
                }}
                defaultValue={bulkEmpType || "0"}
                disabled={!hasEmployeeSavePermission}
                size="small"
                {...(hasEmployeeSavePermission && {
                  onChange: (e) => handleBulkEmpTypeChange(e.target.value),
                })}
              >
                <MenuItem value="0" disabled>Type</MenuItem>
                <MenuItem value="Employees">Employee</MenuItem>
                <MenuItem value="Sub Contractors">Sub Contractor</MenuItem>
              </CustomTextField>

              <CustomTextField
                select
                defaultValue={bulkEmpStatus || "0"}
                disabled={!hasEmployeeSavePermission}
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: '120px' },
                  '& .MuiInputBase-root': {
                    width: { xs: '100%', sm: 'auto' },
                  },
                  order: { xs: 3, sm: 3 }
                }}
                {...(hasEmployeeSavePermission && {
                  onChange: (e) => handleBulkEmpStatusChange(e.target.value),
                })}
              >
                <MenuItem value="0" disabled>Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </CustomTextField>

              <Button
                variant="contained"
                color="main"
                disabled={!hasEmployeeSavePermission}
                sx={{
                  order: { xs: 4, sm: 4 },
                  width: { xs: '100%', sm: 'auto' }
                }}
                {...(hasEmployeeSavePermission && {
                  onClick: () => handleBulkOperations(),
                })}
              >
                Apply Changes
              </Button>
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                order: { xs: 5, sm: 5 }
              }} 
            />
            <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: 1,
                display: "flex",
                gap: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'space-around', sm: 'flex-start' },
                order: { xs: 6, sm: 6 }
              }}
            >
              <Tooltip title="Tag Management">
                <IconButton
                  disabled={!hasEmployeeSavePermission}
                  {...(hasEmployeeSavePermission && {
                    onClick: () => handleOpenTagManagementDlg(),
                  })}
                >
                  <Icon icon="tabler:tag" />
                </IconButton>
              </Tooltip>
              <Divider 
                orientation="vertical" 
                flexItem 
                sx={{ 
                  display: { xs: 'block', sm: 'none' },
                }} 
              />
              <Tooltip title="Delete Selected">
                <IconButton
                  color="error"
                  disabled={!hasEmployeeDeletePermission}
                  {...(hasEmployeeDeletePermission && {
                    onClick: () =>
                      showBulkConfirmation(
                        Object.values(selectedRows).length,
                        `Do you want to delete ${
                          Object.values(selectedRows).length
                        } ${
                          Object.values(selectedRows).length === 1
                            ? "employee"
                            : "employees"
                        }? You won't be able to revert this!`,
                        "Delete",
                        [],
                        `${
                          Object.values(selectedRows).length === 1
                            ? "Yes, delete it"
                            : "Yes, delete them"
                        }`
                      ),
                  })}
                >
                  <Icon icon="tabler:trash" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ height: "75vh" }}>
          <DataGridPro
            editMode="cell"
            // pagination
            rows={filteredRows}
            rowHeight={62}
            columns={columns}
            checkboxSelection
            // pageSizeOptions={[10, 50, { value: -1, label: "Show All" }]}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={handlePaginationModel}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelectionModel) => {
              handleGridSelection(newSelectionModel);
              setRowSelectionModel(newSelectionModel);
            }}
            hideFooterSelectedRowCount
            getRowId={(row) => row.id}
            processRowUpdate={(updatedRow, originalRow) =>
              handleRowDataChange(updatedRow, originalRow)
            }
            onProcessRowUpdateError={handleProcessRowUpdateError}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
            }}
            slots={{
              noRowsOverlay: NoRowsOverlay,
              footer: () => null
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "Fullname", sort: "asc" }],
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                  modifiedBy: false,
                  email: false,
                },
              },
            }}
          />
        </Box>
      </Card>

      {/* Pop up dialogs */}
      <AddEmployeeDialog
        open={openEmployeeDlg}
        handleClose={handleCloseEmployeeDlg}
        fetchEmployees={fetchData}
      />

      {/* Bulk Operation Tag Management Dialog */}
      {/* <TagManagementDialog 
        open={openTagManagementDlg}
        EmpIDs={selectedRows}  
        bulkTags={bulkTags} 
        setBulkTags={setBulkTags}     
        handleClose={handleCloseTagManagementDlg}
        data={tagManagementData}
        toast={toast}
        fetchData={fetchData}
        getEmpByID = {getEmployeeById}
        handleClear = {handleClearSelection}
      /> */}

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={employee?.tags}
        tag={Tag}
        setTag={setTag}
        hasSaveIndividualPermission={hasEmployeeUpdatePersonalDetailPermission}
      />

      {/*<ToastContainer />*/}

      {/* <AddUpdateEmployeeDialog
        open={openNewCustomerDlg}
        steps={steps}
        activeStep={activeStep}
        handleClose={handleCloseNewCustomerDlg}
        handleBack={handleBack}
        handleNext={handleNext}
        renderContent={renderContent}
        Title={modalData.title}
        ButtonText={modalData.btnTxt}
      /> */}

      {/* Export Employee Table */}

      <BulkImport
        open={openBulkImportDlg}
        handleClose={handleCloseBulkImportDlg}
        content={{
          title: "Employees",
          // ImportURL: "/employees/add-bulk-employees",
          ImportURL: "/onboarding/add-employee",          
          customerID: 0,
        }}
        files={EmpFiles}
        loading={btnLoader}
        setLoading={setLoading}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        setFiles={setEmpFiles}
        navigate={navigate}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Employees;
